import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { STORAGE_KEYS } from '#/shared/lib/storage'

import { useManageFieldsActive } from './context'
import { ALL_MANAGE_FIELD_KEYS, MANAGE_FIELD_KEY } from './keys'
import type { ManageFieldKey } from './keys'

type HiddenMap = Partial<Record<ManageFieldKey, true>>

interface ManageFieldsState {
  hidden: HiddenMap
  toggle: (key: ManageFieldKey) => void
  hideAll: () => void
  showAll: () => void
}

const STORAGE_KEY = STORAGE_KEYS.manageFields.key

const allHidden = (): HiddenMap =>
  Object.fromEntries(
    ALL_MANAGE_FIELD_KEYS.map((key) => [key, true]),
  ) as HiddenMap

const DEFAULT_HIDDEN: HiddenMap = {
  [MANAGE_FIELD_KEY.customerCompactView]: true,
}

export const useManageFieldsStore = create<ManageFieldsState>()(
  persist(
    (set) => ({
      hidden: { ...DEFAULT_HIDDEN },
      toggle: (key) =>
        set((state) => {
          const next: HiddenMap = { ...state.hidden }
          if (next[key]) delete next[key]
          else next[key] = true
          return { hidden: next }
        }),
      hideAll: () => set({ hidden: allHidden() }),
      showAll: () => set({ hidden: { ...DEFAULT_HIDDEN } }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({ hidden: state.hidden }),
    },
  ),
)

export function useIsFieldHidden(key: ManageFieldKey): boolean {
  const active = useManageFieldsActive()
  const hidden = useManageFieldsStore((state) => Boolean(state.hidden[key]))
  return active && hidden
}

export function useFieldsVisible<TKey extends ManageFieldKey>(
  keys: readonly TKey[],
): Readonly<Record<TKey, boolean>> {
  const active = useManageFieldsActive()
  const hidden = useManageFieldsStore((state) => state.hidden)
  const out = {} as Record<TKey, boolean>
  for (const key of keys) out[key] = !(active && hidden[key])
  return out
}

export function getHiddenFields(): HiddenMap {
  return useManageFieldsStore.getState().hidden
}

export interface CompactViewState {
  enabled: boolean
  compact: boolean
  toggle: () => void
}

export function useCompactView(): CompactViewState {
  const active = useManageFieldsActive()
  const storeOn = useManageFieldsStore(
    (state) => !state.hidden[MANAGE_FIELD_KEY.customerCompactView],
  )
  const [localCompact, setLocalCompact] = useState(active ? storeOn : false)
  useEffect(() => {
    if (active) setLocalCompact(storeOn)
  }, [active, storeOn])
  const enabled = active ? storeOn : true
  const compact = enabled ? localCompact : false
  const toggle = () => setLocalCompact((prev) => !prev)
  return { enabled, compact, toggle }
}
